import { Form, Image, Select, Upload } from "antd";
import { ReactElement, useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { ReactSVG } from "react-svg";
import moment from "moment";

import "./add-edit-document.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import instance from "src/store/axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { LOADING } from "src/store/actions";
import {
  getStandingDataOptions,
  isImageFile,
} from "src/utils/helper/functions";
import CustomModal from "src/components/custom-modal/custom-modal";
import PdfViewer from "src/components/pdf-viewer";
import CustomInput from "src/components/custom-input/custom-input.component";
import Label from "src/components/label/label.component";
import CustomTable from "src/components/custom-table/custom-table";
import DeleteModal from "src/components/delete-modal/delete-modal";

const { Dragger } = Upload;

interface AddEditDocumentProps {
  standingData: any;
  accountKycDetails: any;
}

const AddEditDocument: React.FC<AddEditDocumentProps> = ({
  standingData,
  accountKycDetails,
}): ReactElement => {
  const { accountId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const user = useSelector((state: any) => state?.user);

  const [form] = Form.useForm();
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});
  const [documents, setDocuments] = useState<any>([]);
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);

  useEffect(() => {
    if (
      accountKycDetails?.id &&
      (accountKycDetails?.activeDocumentsId ||
        accountKycDetails?.pendingDocumentsId)
    ) {
      getDocumentsApi();
    }
  }, [accountKycDetails]);

  const getDocumentsApi = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const documentId = isApproveCase
        ? accountKycDetails?.pendingDocumentsId
        : accountKycDetails?.activeDocumentsId ||
        accountKycDetails?.pendingDocumentsId;

      const response = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/account/${accountKycDetails?.id}?accountDocumentId=${documentId}`
      );

      setDocuments(response.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onSubmit = async (values: any) => {
    dispatch({ type: LOADING, payload: true });
    const formData = new FormData();
    formData.append("file", values.file.file);
    formData.append("documentType", values.type);
    if (accountKycDetails?.accountStatus?.description === "Pending") {
      formData.append("isPendingUpload", "true");
    }
    formData.append("branch", "SA");

    if (values.description) {
      formData.append("otherDocDescription", values.description);
    }
    if (values.name) {
      formData.append("otherDocName", values.name);
    }

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/${accountId}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Document added successfully");
      setOpenAddEditModal(false);
      form.resetFields();
      setEditingDocument(null);
      getDocumentsApi();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
    dispatch({ type: LOADING, payload: false });
  };

  const beforeUpload = (file: any) => {
    const isImageOrPdf =
      file.type?.toLowerCase() === "image/png" ||
      file.type?.toLowerCase() === "image/gif" ||
      file.type?.toLowerCase() === "image/jpg" ||
      file.type?.toLowerCase() === "image/jpeg" ||
      file.type?.toLowerCase() === "application/pdf";

    if (!isImageOrPdf) {
      toast.error(
        "Error: Only PDF and image files are allowed for upload. Please try again with a valid file format!"
      );
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleAddDocument = () => {
    setEditingDocument(null);
    form.resetFields();
    setOpenAddEditModal(true);
  };

  const handleEditDocument = (record: any) => {
    setEditingDocument(record);
    form.setFieldsValue({
      type: record?.documentType?.id,
      name: record?.otherDocName,
      description: record?.otherDocDescription,
    });
    setOpenAddEditModal(true);
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    dispatch({ type: LOADING, payload: true });
    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/${documentToDelete.id}/delete`
      );
      toast.success("Document deleted successfully");
      setOpenDeleteModal(false);
      setDocumentToDelete(null);
      getDocumentsApi();
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }
    dispatch({ type: LOADING, payload: false });
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "File type",
      dataIndex: "documentType",
      key: "documentName",
      width: 270,
      sorter: (a, b) =>
        String(a?.documentType?.description).localeCompare(
          String(b?.documentType?.description)
        ),
      fixed: "left",
      render: (_, record) => (
        <span>
          {record?.documentType?.description === "Other Documents"
            ? `${record?.otherDocName || "Other document"}`
            : record?.documentType?.description}
        </span>
      ),
    },
    {
      title: "Document",
      dataIndex: "storagePath",
      key: "storagePath",
      render: (text, record) => {
        const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
        const ext = record.documentExtension?.toLowerCase();
        const isImage = imageExtensions.includes(ext);

        return isImage ? (
          <Image src={text} width={"80px"} height={"50px"} alt="" />
        ) : (
          <span
            className="cursor-pointer text-blue-500 underline"
            onClick={() => {
              setSelectedDocument(record);
              setOpenPdfViewerModal(true);
            }}
          >
            {record.documentName}
          </span>
        );
      },
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      sorter: (a, b) =>
        moment(a?.createdAt).isBefore(moment(b?.createdAt)) ? -1 : 1,
      render: (text) => (
        <span className="text-gray-500">
          {moment(text).format("MMM DD, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Updated at",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 190,
      sorter: (a, b) =>
        moment(a?.updatedAt).isBefore(moment(b?.updatedAt)) ? -1 : 1,
      render: (text) => (
        <span className="text-gray-500">
          {moment(text).format("MMM DD, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="actions flex align-center gap-3">
          <ReactSVG
            src={"/images/edit.svg"}
            className="cursor-pointer"
            onClick={() => handleEditDocument(record)}
          />
          <ReactSVG
            src={"/images/delete.svg"}
            className="cursor-pointer delete-icon"
            onClick={() => {
              setDocumentToDelete(record);
              setOpenDeleteModal(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="add-edit-document flex flex-col mt-5">
      <div className="flex justify-between items-center mb-5">
        <div className="flex gap-3 items-center">
          <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
          <h3 className="text-lg font-semibold">Documents</h3>
        </div>
        <button
          onClick={handleAddDocument}
          className="rounded-lg bg-blue-500 px-4 py-2 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
        >
          Add Document
        </button>
      </div>

      <CustomTable columns={columns} data={documents} showPagination={false} />

      {accountKycDetails?.accountStatus?.description === "Pending" && documents?.length === 0 && (<div className="flex justify-end mt-5 items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/home/customers-list")}
          className="rounded-lg bg-white px-8 py-2 text-center outline-0 cursor-pointer text-sm font-semibold border-[1px] border-blue-500 text-blue-500 hover:bg-blue-50 transition-colors"
        >
          Close
        </button>

      </div>)}

      {/* Add/Edit Document Modal */}
      <CustomModal
        isVisible={openAddEditModal}
        centered
        width={600}
        showClose={true}
        onClose={() => {
          setOpenAddEditModal(false);
          form.resetFields();
          setEditingDocument(null);
        }}
        className="add-edit-document-modal"
      >
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">
            {editingDocument ? "Edit Document" : "Add Document"}
          </h3>
          <Form
            form={form}
            className="add-edit-form flex flex-col gap-4"
            onFinish={onSubmit}
          >
            <div className="field w-full flex flex-col">
              <Label text="Doc. type" required />
              <Form.Item
                name="type"
                rules={[
                  {
                    required: true,
                    message: "Please select the document type!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Document"
                  disabled={!!editingDocument}
                  options={[
                    ...getStandingDataOptions(
                      standingData,
                      "DocumentType",
                      ["sa", "gc"],
                      false
                    ).filter((option: any) => {
                      // When editing, allow the current document type
                      if (editingDocument?.documentType?.id === option.value) {
                        return true;
                      }
                      // Filter out document types that are already uploaded
                      const uploadedTypeIds = documents.map(
                        (doc: any) => doc.documentType?.id
                      )?.filter((id: any) => id !== 196);
                      return !uploadedTypeIds.includes(option.value);
                    }),
                  ]}
                />
              </Form.Item>
            </div>

            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
              noStyle
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === 196 ? (
                  <div className="field w-full flex flex-col">
                    <Label text="Doc. name" required />
                    <Form.Item
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the document name!",
                        },
                      ]}
                      noStyle
                    >
                      <CustomInput placeholder="Enter the document name" />
                    </Form.Item>
                  </div>
                ) : null
              }
            </Form.Item>

            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
              noStyle
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === 196 ? (
                  <div className="field w-full flex flex-col">
                    <Label text="Doc. description" required />
                    <Form.Item
                      name="description"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the document description!",
                        },
                      ]}
                      noStyle
                    >
                      <CustomInput placeholder="Enter the document description" />
                    </Form.Item>
                  </div>
                ) : null
              }
            </Form.Item>

            <div className="fields flex w-full upload-container">
              <Form.Item
                name="file"
                rules={[
                  {
                    required: !editingDocument,
                    message: "Please upload the file",
                  },
                ]}
                className="w-full"
              >
                <Dragger
                  beforeUpload={beforeUpload}
                  customRequest={() => false}
                  multiple={false}
                  accept="image/*,.pdf"
                  maxCount={1}
                  className="w-full"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              </Form.Item>
            </div>

            {editingDocument && isImageFile(editingDocument?.documentExtension) && (
              <Image
                src={editingDocument?.storagePath}
                width={"80px"}
                height={"50px"}
                alt="kamco document"
              />
            )}

            {editingDocument && !isImageFile(editingDocument?.documentExtension) && (
              <span
                className="underline cursor-pointer text-blue-500"
                onClick={() => {
                  setSelectedDocument(editingDocument);
                  setOpenPdfViewerModal(true);
                }}
              >
                View current document
              </span>
            )}

            <div className="flex w-full items-center justify-end mt-4 gap-3">
              <button
                type="button"
                onClick={() => {
                  setOpenAddEditModal(false);
                  form.resetFields();
                  setEditingDocument(null);
                }}
                className="rounded-lg bg-white px-6 py-2 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 px-6 py-2 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
              >
                Save
              </button>
            </div>
          </Form>
        </div>
      </CustomModal>

      {/* PDF Viewer Modal */}
      <CustomModal
        isVisible={openPdfViewerModal}
        centered
        width={1100}
        showClose={false}
        onClose={() => {
          setOpenPdfViewerModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <PdfViewer fileUrl={selectedDocument?.storagePath} />
        </div>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <CustomModal
        isVisible={openDeleteModal}
        centered
        width={450}
        onClose={() => {
          setOpenDeleteModal(false);
          setDocumentToDelete(null);
        }}
        className="delete-modal"
      >
        <DeleteModal
          onCancel={() => {
            setOpenDeleteModal(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleDeleteDocument}
          text="Delete document!"
          subText="Are you sure you want to delete this document?"
        />
      </CustomModal>
    </div>
  );
};

export default AddEditDocument;
